import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FaFacebook, FaGoogle, FaInstagram } from 'react-icons/fa';
import { useIsMobile } from '../../../hooks/use-mobile';
import {
  API_SN_DELETE,
  API_GET_LINKS,
  API_KEY,
  APP_GET_CLIENT,
  TIKTOK_OAUTH_START_URL,
  TIKTOK_ENABLED
} from '../../../utils';
import {
  getBingConfig,
  exchangeBingCode,
  exchangeBingCodeInBrowser,
  saveBingConnection
} from '../../../services/listingApi';
import './style.css';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import {
  getGoogleOAuthRedirectUri,
  initiateGoogleOAuth
} from '../../../services/googleOAuth';
import { initiateFacebookOAuth } from '../../../services/facebookOAuth';
import { selectedClientSelector } from '../../../store/slices/selectedClient';
import BingLogo from '../../../Icons/bing.svg';

import {
  Home,
  Star,
  FileText,
  Image,
  Bell,
  LogOut,
  Menu,
  Facebook,
  Instagram,
  Check,
  X,
  CalendarDays,
  Building2,
  Plus,
  Link2,
  CheckCircle2,
  Unplug,
  MapPin,
  Loader2,
  AlertCircle,
  Crown
} from 'lucide-react';

const OAUTH_SUCCESS_LOCAL_STORAGE_KEY = 'oauth_social_connect_success_ls';

const normalizeUrl = (value) => (value || '').trim().replace(/\/+$/, '');

const isEnabled = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const extractApiErrorMessage = (error, fallbackMessage) => {
  const apiData = error?.response?.data;
  return (
    apiData?.details ||
    apiData?.error_description ||
    apiData?.error ||
    apiData?.message ||
    error?.message ||
    fallbackMessage
  );
};

const createPkcePair = async () => {
  const random = new Uint8Array(32);
  window.crypto.getRandomValues(random);

  const codeVerifier = Array.from(random)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  const data = new TextEncoder().encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  const bytes = Array.from(new Uint8Array(digest));
  const base64 = btoa(String.fromCharCode(...bytes));
  const codeChallenge = base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');

  return { codeVerifier, codeChallenge };
};

const TRUTHY_WEBVIEW_VALUES = ['1', 'true', 'yes', 'on'];

const Links = ({ selectedClient, userInfo }) => {
  const { user } = useUser();
  const selectedClientState = useSelector(selectedClientSelector);
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isWebview = TRUTHY_WEBVIEW_VALUES.includes(
    new URLSearchParams(location.search).get('webview')?.toLowerCase() || ''
  );
  const [links, setLinks] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnectNetwork, setDisconnectNetwork] = useState('');
  const [disconnectClientId, setDisconnectClientId] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successTitle, setSuccessTitle] = useState('Success!');
  const [successDescription, setSuccessDescription] = useState('Disconnected!');
  const [reloadOnSuccessClose, setReloadOnSuccessClose] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState('Operation Failed');
  const [errorMessage, setErrorMessage] = useState('');
  const [bingConnected, setBingConnected] = useState(false);
  const [bingConnecting, setBingConnecting] = useState(false);
  const [bingAccountName, setBingAccountName] = useState('');
  const [tiktokMockDisconnected, setTiktokMockDisconnected] = useState(false);
  const [tiktokPremium, setTiktokPremium] = useState(false);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const googlePopupWatcherRef = useRef(null);
  const oauthReloadingRef = useRef(false);
  const reduxClient = selectedClientState?.selectedClient;
  const appUid = selectedClient?.appUid || reduxClient?.appUid;
  const clientTiktokPremium =
    selectedClient?.tiktokPremium ?? reduxClient?.tiktokPremium;

  // Check if user has pro plan
  const hasProPlan = user?.publicMetadata?.plan === 'pro';

  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const showBing = process.env.REACT_APP_SHOW_BING === 'true';
  const openSuccessModal = (title, description, shouldReload = false) => {
    setSuccessTitle(title);
    setSuccessDescription(description);
    setReloadOnSuccessClose(shouldReload);
    setSuccessModalOpen(true);
  };

  const openErrorModal = (title, description) => {
    setErrorTitle(title);
    setErrorMessage(description);
    setErrorModalOpen(true);
  };

  const fetchClientLinks = async (clientId) => {
    const response = await fetch(
      API_GET_LINKS + new URLSearchParams({ clientId })
    );
    const data = await response.json();
    const fetchedLinks = data?.Links || null;
    setLinks(fetchedLinks);
    return fetchedLinks;
  };

  // Listener para mensajes del popup de OAuth
  useEffect(() => {
    const getAllowedOrigins = () => {
      const origins = [window.location.origin];
      try {
        const redirectUri = getGoogleOAuthRedirectUri();
        if (redirectUri) {
          origins.push(new URL(redirectUri).origin);
        }
      } catch (_) {}
      return Array.from(new Set(origins));
    };

    const allowedOrigins = getAllowedOrigins();

    const getNetworkNameByType = (type) => {
      if (type === 'GOOGLE_OAUTH_SUCCESS') return 'Google Business';
      if (type === 'FACEBOOK_OAUTH_SUCCESS') return 'Facebook';
      if (type === 'INSTAGRAM_OAUTH_SUCCESS') return 'Instagram';
      return null;
    };

    const handleOAuthMessage = (event) => {
      // Verificar que el mensaje viene de nuestro dominio
      if (!allowedOrigins.includes(event.origin)) return;

      const networkName =
        event?.data?.networkName || getNetworkNameByType(event?.data?.type);

      if (networkName) {
        if (oauthReloadingRef.current) return;
        oauthReloadingRef.current = true;
        // Recargar la página para mostrar la nueva conexión
        window.location.reload();
      }
    };

    window.addEventListener('message', handleOAuthMessage);

    return () => {
      window.removeEventListener('message', handleOAuthMessage);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (googlePopupWatcherRef.current) {
        clearInterval(googlePopupWatcherRef.current);
      }
    };
  }, []);

  const isGoogleConnectedFromLinks = (linksData) =>
    Boolean(
      linksData?.GMB_Google_2_URL || linksData?.Google_Business_Listing_URL
    );

  const isTikTokConnectedFromLinks = (linksData) =>
    Boolean(
      linksData?.TikTok_URL ||
        linksData?.TikTok_Profile_URL ||
        linksData?.TikTok_Account_Name ||
        linksData?.TikTok_Username ||
        linksData?.TikTok_Handle ||
        linksData?.TikTok_Open_ID
    );

  const verifyGoogleConnectionAndReload = async () => {
    const clientId = selectedClient?.id;
    if (!clientId) return false;

    const maxRetries = 8;
    const retryDelayMs = 1200;

    for (let attempt = 0; attempt < maxRetries; attempt += 1) {
      try {
        const response = await fetch(
          API_GET_LINKS + new URLSearchParams({ clientId })
        );
        const data = await response.json();
        const fetchedLinks = data?.Links;

        if (fetchedLinks) {
          setLinks(fetchedLinks);
        }

        if (isGoogleConnectedFromLinks(fetchedLinks)) {
          if (oauthReloadingRef.current) return true;
          oauthReloadingRef.current = true;
          window.location.reload();
          return true;
        }
      } catch (_) {}

      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    }

    return false;
  };

  useEffect(() => {
    const handleOauthSuccessFromStorage = () => {
      try {
        const storedValue = localStorage.getItem(
          OAUTH_SUCCESS_LOCAL_STORAGE_KEY
        );
        if (!storedValue) return;

        const parsedValue = JSON.parse(storedValue);
        if (!parsedValue?.networkName) {
          localStorage.removeItem(OAUTH_SUCCESS_LOCAL_STORAGE_KEY);
          return;
        }

        localStorage.removeItem(OAUTH_SUCCESS_LOCAL_STORAGE_KEY);
        if (oauthReloadingRef.current) return;
        oauthReloadingRef.current = true;
        window.location.reload();
      } catch (_) {
        localStorage.removeItem(OAUTH_SUCCESS_LOCAL_STORAGE_KEY);
      }
    };

    // In case OAuth finished just before this effect was attached.
    handleOauthSuccessFromStorage();

    const handleStorage = (event) => {
      if (event.key !== OAUTH_SUCCESS_LOCAL_STORAGE_KEY) return;
      handleOauthSuccessFromStorage();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Función para iniciar OAuth de Google
  const handleConnectGoogle = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = getGoogleOAuthRedirectUri();

    // State incluye el clientId de la plataforma para saber a quien asociar los tokens
    const state = encodeURIComponent(
      JSON.stringify({
        clientId: selectedClient.id,
        platform: 'adlersuite',
        openerOrigin: window.location.origin
      })
    );

    const popup = initiateGoogleOAuth(clientId, redirectUri, state);

    if (!popup) {
      setErrorMessage(
        'Unable to open Google OAuth popup. Please allow popups and try again.'
      );
      setErrorModalOpen(true);
      return;
    }

    if (googlePopupWatcherRef.current) {
      clearInterval(googlePopupWatcherRef.current);
    }

    googlePopupWatcherRef.current = setInterval(async () => {
      if (!popup.closed) return;

      clearInterval(googlePopupWatcherRef.current);
      googlePopupWatcherRef.current = null;

      await verifyGoogleConnectionAndReload();
    }, 700);
  };

  // Función para iniciar OAuth de Facebook
  const handleConnectFacebook = () => {
    initiateFacebookOAuth(selectedClient.id);
  };

  // Función para iniciar OAuth de Instagram (vía Facebook OAuth)
  const handleConnectInstagram = () => {
    initiateFacebookOAuth(selectedClient.id, 'instagram');
  };

  const handleConnectTikTok = () => {
    if (!tiktokPremium) {
      setPremiumModalOpen(true);
      return;
    }

    if (!selectedClient?.id) {
      openErrorModal('Connection Failed', 'Please select a client first.');
      return;
    }

    const tiktokOAuthStartUrl = normalizeUrl(TIKTOK_OAUTH_START_URL);
    if (!tiktokOAuthStartUrl) {
      openErrorModal(
        'Connection Failed',
        'TikTok OAuth start URL is not configured. Contact support.'
      );
      return;
    }

    sessionStorage.setItem('tiktok_oauth_clientId', selectedClient.id);
    window.location.assign(
      `${tiktokOAuthStartUrl}?clientId=${encodeURIComponent(selectedClient.id)}`
    );
  };

  // Función para iniciar OAuth de Bing Places
  const handleConnectBing = async () => {
    if (!selectedClient?.id) {
      setErrorTitle('Connection Failed');
      setErrorMessage('Please select a client first.');
      setErrorModalOpen(true);
      return;
    }

    try {
      setBingConnecting(true);

      if (!window.crypto?.subtle) {
        setErrorTitle('Connection Failed');
        setErrorMessage(
          'Your browser does not support secure Bing OAuth PKCE.'
        );
        setErrorModalOpen(true);
        setBingConnecting(false);
        return;
      }

      const config = await getBingConfig();
      const clientId = config.clientId;
      const tenantId = config.tenantId || 'common';
      const tokenExchangeMode = config.tokenExchangeMode || 'browser';
      const scope =
        config.scope ||
        'openid profile email offline_access https://ads.microsoft.com/msads.manage';

      const { codeVerifier, codeChallenge } = await createPkcePair();

      const redirectUri = `${window.location.origin}/bing-callback`;
      const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${encodeURIComponent(
        clientId
      )}&response_type=code&redirect_uri=${encodeURIComponent(
        redirectUri
      )}&response_mode=query&scope=${encodeURIComponent(
        scope
      )}&code_challenge=${encodeURIComponent(
        codeChallenge
      )}&code_challenge_method=S256`;

      const popup = window.open(authUrl, 'BingOAuth', 'width=600,height=700');

      const handleMessage = async (event) => {
        if (event.data?.type !== 'BING_AUTH_CALLBACK') return;
        window.removeEventListener('message', handleMessage);
        if (popup) popup.close();

        const { code } = event.data;
        if (!code) {
          setErrorTitle('Connection Failed');
          setErrorMessage('No authorization code received from Microsoft.');
          setErrorModalOpen(true);
          setBingConnecting(false);
          return;
        }

        try {
          let result;
          if (tokenExchangeMode === 'browser') {
            result = await exchangeBingCodeInBrowser(
              code,
              redirectUri,
              codeVerifier,
              clientId,
              tenantId,
              scope
            );
          } else {
            try {
              result = await exchangeBingCode(code, redirectUri, codeVerifier);
            } catch (backendErr) {
              const backendErrorMessage = extractApiErrorMessage(
                backendErr,
                ''
              );
              const normalized = backendErrorMessage.toLowerCase();
              const isSpaRedemptionError =
                normalized.includes('aadsts90023') ||
                normalized.includes('single-page application') ||
                normalized.includes('cross-origin requests');

              if (!isSpaRedemptionError) {
                throw backendErr;
              }

              result = await exchangeBingCodeInBrowser(
                code,
                redirectUri,
                codeVerifier,
                clientId,
                tenantId,
                scope
              );
            }
          }

          await saveBingConnection(
            selectedClient.id,
            result.account?.id || '',
            result.account?.displayName || '',
            result.account?.email || '',
            result.accessToken,
            result.refreshToken || '',
            result.scope || ''
          );

          const connectedName =
            result.account?.displayName ||
            result.account?.email ||
            'Connected Microsoft Account';

          setBingConnected(true);
          setBingAccountName(connectedName);
          history.go(0);
        } catch (err) {
          console.error('Bing exchange error:', err);
          setErrorTitle('Connection Failed');
          setErrorMessage(
            extractApiErrorMessage(
              err,
              'Failed to connect Bing Places account.'
            )
          );
          setErrorModalOpen(true);
        }

        setBingConnecting(false);
      };

      window.addEventListener('message', handleMessage);

      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        setBingConnecting(false);
      }, 120000);
    } catch (err) {
      console.error('Bing connect error:', err);
      setErrorTitle('Connection Failed');
      setErrorMessage(
        extractApiErrorMessage(err, 'Failed to start Bing Places connection.')
      );
      setErrorModalOpen(true);
      setBingConnecting(false);
    }
  };

  // Function to disconnect social networks
  const disconnectSocialNetwork = async (network, clientId) => {
    setDisconnectNetwork(network);
    setDisconnectClientId(clientId);
    setDisconnectModalOpen(true);
  };

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    setErrorMessage('');

    // Mock disconnect flow for TikTok while backend lambda is pending.
    if (disconnectNetwork === 'tiktok') {
      setTimeout(() => {
        setIsDisconnecting(false);
        setDisconnectModalOpen(false);
        setTiktokMockDisconnected(true);
        openSuccessModal(
          'Success!',
          'TikTok disconnected (mock). Backend lambda pending implementation.',
          false
        );
      }, 600);
      return;
    }

    try {
      const response = await axios.delete(API_SN_DELETE, {
        data: {
          socialNetwork: disconnectNetwork,
          id: disconnectClientId
        },
        timeout: 30000, // 30 segundos timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        setDisconnectModalOpen(false);
        setSuccessModalOpen(true);
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error('🔴 [DISCONNECT] Error occurred:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code,
        isTimeout: error.code === 'ECONNABORTED',
        timestamp: new Date().toISOString(),
        fullError: error
      });

      setIsDisconnecting(false);
      setDisconnectModalOpen(false);

      // Mensajes de error más descriptivos
      let userMessage = 'Failed to disconnect the social network. ';

      if (error.code === 'ECONNABORTED') {
        userMessage +=
          'The request timed out after 30 seconds. Please try again.';
      } else if (error.response?.status === 504) {
        userMessage +=
          'Gateway timeout (504). The server took too long to respond. Please try again in a few moments.';
      } else if (error.response?.status === 500) {
        userMessage +=
          'Internal server error (500). Please contact support if this persists.';
      } else if (error.response?.data?.error) {
        userMessage += error.response.data.error;
      } else if (error.message) {
        userMessage += error.message;
      } else {
        userMessage += 'An unknown error occurred. Please try again.';
      }

      setErrorMessage(userMessage);
      setErrorTitle('Disconnection Failed');
      setErrorModalOpen(true);
      return;
    }

    setIsDisconnecting(false);
  };

  const handleSuccessClose = () => {
    setSuccessModalOpen(false);
    history.go(0);
  };

  useEffect(() => {
    let cancelled = false;

    const loadAppSettings = async () => {
      setTiktokPremium(isEnabled(clientTiktokPremium));

      if (!appUid) return;

      try {
        const response = await axios.get(
          `${APP_GET_CLIENT}?uid=${encodeURIComponent(appUid)}`,
          { headers: { 'X-API-KEY': API_KEY } }
        );
        const responseBody = response?.data?.body;
        const appSettings = Array.isArray(responseBody)
          ? responseBody[0]
          : responseBody?.Item || responseBody;
        if (!cancelled) {
          setTiktokPremium(
            isEnabled(appSettings?.tiktokPremium) ||
              isEnabled(clientTiktokPremium)
          );
        }
      } catch (error) {
        console.error('Error loading TikTok premium setting:', error);
      }
    };

    loadAppSettings();

    return () => {
      cancelled = true;
    };
  }, [appUid, clientTiktokPremium]);

  useEffect(() => {
    if (!links) {
      const clientSocialNetworksLinks = async (clientId) => {
        try {
          const { Links } = await (
            await fetch(API_GET_LINKS + new URLSearchParams({ clientId }))
          ).json();
          if (links === null) {
            setLinks(Links);
          }
        } catch (error) {
          console.error('The link have an error', error);
        }
      };
      if (selectedClient.id && links === null) {
        clientSocialNetworksLinks(selectedClient.id);
      }
    }
  }, [selectedClient.id, links]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('connected') !== 'tiktok') {
      return;
    }

    openSuccessModal('Success!', 'TikTok account connected.', false);
    history.replace('/client');

    if (selectedClient?.id) {
      // Poll until the clients table reflects the new tiktok connection,
      // then reload — same pattern as Google.
      const maxRetries = 8;
      const retryDelayMs = 1200;
      let attempt = 0;

      const poll = async () => {
        try {
          const response = await fetch(
            API_GET_LINKS + new URLSearchParams({ clientId: selectedClient.id })
          );
          const data = await response.json();
          const fetchedLinks = data?.Links;
          if (fetchedLinks) setLinks(fetchedLinks);
          if (isTikTokConnectedFromLinks(fetchedLinks)) {
            window.location.reload();
            return;
          }
        } catch (_) {}

        attempt += 1;
        if (attempt < maxRetries) {
          setTimeout(poll, retryDelayMs);
        } else {
          // Reload anyway so the page re-fetches the client from Redux
          window.location.reload();
        }
      };

      setTimeout(poll, retryDelayMs);
    }
  }, [history, location.search, selectedClient?.id]);

  useEffect(() => {
    const socialBing = selectedClient?.socialnetworks?.bing;
    const name =
      socialBing && typeof socialBing === 'object'
        ? socialBing.accountName || socialBing.accountEmail || ''
        : '';

    setBingConnected(!!socialBing);
    setBingAccountName(name);
  }, [selectedClient?.id, selectedClient?.socialnetworks?.bing]);

  useEffect(() => {
    setTiktokMockDisconnected(false);
  }, [selectedClient?.id]);

  const tiktokConnection = selectedClient?.socialnetworks?.tiktok;
  const tiktokConnectedFromClient = Boolean(
    tiktokConnection === true ||
      (tiktokConnection &&
        typeof tiktokConnection === 'object' &&
        Object.keys(tiktokConnection).length > 0) ||
      selectedClient?.socialnetworks?.tiktokAccountName ||
      selectedClient?.socialnetworks?.tiktokUsername ||
      selectedClient?.socialnetworks?.tiktokHandle ||
      selectedClient?.socialnetworks?.tiktokOpenId
  );
  const tiktokConnected =
    !tiktokMockDisconnected &&
    (tiktokConnectedFromClient || isTikTokConnectedFromLinks(links));
  const tiktokDisplayName =
    (typeof tiktokConnection === 'object' &&
      (tiktokConnection.accountName ||
        tiktokConnection.username ||
        tiktokConnection.handle ||
        tiktokConnection.openId)) ||
    selectedClient?.socialnetworks?.tiktokPage ||
    selectedClient?.socialnetworks?.tiktokAccountName ||
    selectedClient?.socialnetworks?.tiktokUsername ||
    selectedClient?.socialnetworks?.tiktokHandle ||
    links?.TikTok_Account_Name ||
    links?.TikTok_Username ||
    links?.TikTok_Handle ||
    (tiktokConnected ? 'Connected' : 'Not connected');
  const tiktokProfileUrl = links?.TikTok_URL || links?.TikTok_Profile_URL;

  return (
    <>
      <Card className="shadow-md rounded-2xl border-0">
        <CardHeader className="flex flex-col items-stretch gap-4 pb-4 pt-5 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-center sm:justify-between sm:pt-6">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Link2 className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold leading-tight break-words">
                Connected Accounts
              </CardTitle>
              <CardDescription className="text-sm sm:text-base mt-1 break-words">
                Your social media and business listings
              </CardDescription>
            </div>
          </div>

          <div className="relative w-full sm:w-auto">
            {!isWebview && (
              <Button
                type="button"
                size="lg"
                onClick={() => setShowDropdown(!showDropdown)}
                className="w-full sm:w-auto justify-center gap-2 sm:gap-3 h-11 sm:h-12 px-4 sm:px-6 text-sm sm:text-base font-semibold rounded-xl">
                <Plus className="h-5 w-5" />
                Add Account
              </Button>
            )}

            {showDropdown && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowDropdown(false)}
                />
                <div className="absolute left-0 right-0 mt-2 w-full sm:left-auto sm:right-0 sm:w-64 bg-white rounded-xl border border-gray-200 shadow-xl z-50 p-2">
                  <button
                    type="button"
                    onClick={() => {
                      handleConnectFacebook();
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <Facebook className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium">Facebook</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      handleConnectGoogle();
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="h-8 w-8 rounded-full bg-white border border-gray-200 flex items-center justify-center">
                      <svg className="h-4 w-4" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                    </div>
                    <span className="font-medium">Google Business</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      handleConnectInstagram();
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center">
                      <Instagram className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium">Instagram</span>
                  </button>

                  {TIKTOK_ENABLED && (
                    <button
                      type="button"
                      onClick={() => {
                        handleConnectTikTok();
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center">
                        <svg
                          className="h-4 w-4 text-white"
                          viewBox="0 0 24 24"
                          fill="currentColor">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                        </svg>
                      </div>
                      <span className="font-medium">TikTok</span>
                    </button>
                  )}

                  {showBing && (
                    <button
                      type="button"
                      onClick={() => {
                        handleConnectBing();
                        setShowDropdown(false);
                      }}
                      disabled={bingConnecting}
                      className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <div className="h-8 w-8 rounded-full bg-white shadow-sm border border-gray-200 flex items-center justify-center overflow-hidden">
                        <img
                          src={BingLogo}
                          alt="Bing"
                          className="h-6 w-6 object-contain"
                        />
                      </div>
                      <span className="font-medium">
                        {bingConnecting ? 'Connecting Bing...' : 'Bing Places'}
                      </span>
                    </button>
                  )}

                  {/* <div className="h-px bg-gray-200 my-2" /> */}

                  {/* <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 flex items-center justify-center text-[#00A4A6]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        viewBox="0 0 16 16">
                        <path d="M8.35 5.046a.615.615 0 0 0-.54.575c-.009.13-.006.14.289.899.67 1.727.833 2.142.86 2.2q.101.215.277.395c.089.092.148.141.247.208.176.117.262.15.944.351.664.197 1.026.327 1.338.482.405.201.688.43.866.7.128.195.242.544.291.896.02.137.02.44 0 .564-.041.27-.124.495-.252.684-.067.1-.044.084.055-.039.278-.346.562-.938.707-1.475a4.42 4.42 0 0 0-2.14-5.028 70 70 0 0 0-.888-.465l-.53-.277-.353-.184c-.16-.082-.266-.138-.345-.18-.368-.192-.523-.27-.568-.283a1 1 0 0 0-.194-.03z" />
                        <path d="M9.152 11.493a3 3 0 0 0-.135.083 320 320 0 0 0-1.513.934l-.8.496c-.012.01-.587.367-.876.543a1.9 1.9 0 0 1-.732.257c-.12.017-.349.017-.47 0a1.9 1.9 0 0 1-.884-.358 2.5 2.5 0 0 1-.365-.364 1.9 1.9 0 0 1-.34-.76 1 1 0 0 0-.027-.121c-.005-.006.004.092.022.22.018.132.057.324.098.489a4.1 4.1 0 0 0 2.487 2.796c.359.142.72.23 1.114.275.147.016.566.023.72.011a4.1 4.1 0 0 0 1.956-.661l.235-.149.394-.248.258-.163 1.164-.736c.51-.32.663-.433.9-.665.099-.097.248-.262.255-.283.002-.005.028-.046.059-.091a1.64 1.64 0 0 0 .25-.682c.02-.124.02-.427 0-.565a3 3 0 0 0-.213-.758c-.15-.314-.47-.6-.928-.83a2 2 0 0 0-.273-.12c-.006 0-.433.26-.948.58l-1.113.687z" />
                        <path d="m3.004 12.184.03.129c.089.402.245.693.515.963a1.82 1.82 0 0 0 1.312.543c.361 0 .673-.09.994-.287l.472-.29.373-.23V5.334c0-1.537-.003-2.45-.008-2.521a1.82 1.82 0 0 0-.535-1.177c-.097-.096-.18-.16-.427-.33L4.183.24c-.239-.163-.258-.175-.33-.2a.63.63 0 0 0-.842.464c-.009.042-.01.603-.01 3.646l.003 8.035Z" />
                      </svg>
                    </div>
                    <span className="font-medium">Bing Places</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center">
                      <svg
                        className="h-4 w-4 text-white"
                        viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                      </svg>
                    </div>
                    <span className="font-medium">TikTok</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 flex items-center justify-center text-black">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor">
                        <path d="M20 6.009h-2.829C15.211 4.675 12.813 4 10 4s-5.212.675-7.171 2.009H0c.428.42.827 1.34.993 2.04A4.954 4.954 0 0 0 0 11.008c0 2.757 2.243 5 5 5a4.97 4.97 0 0 0 3.423-1.375L10 17l1.577-2.366A4.97 4.97 0 0 0 15 16.01c2.757 0 5-2.243 5-5 0-1.112-.377-2.13-.993-2.96.166-.7.565-1.62.993-2.04z" />
                      </svg>
                    </div>
                    <span className="font-medium">TripAdvisor</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium">Apple Maps</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 rounded-full bg-[#0061A8] flex items-center justify-center">
                      <span className="text-white text-xs font-bold">BBB</span>
                    </div>
                    <span className="font-medium">Better Business Bureau</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 flex items-center justify-center text-[#FF1A1A]">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 16 16"
                        fill="currentColor">
                        <path d="m4.188 10.095.736-.17.073-.02A.813.813 0 0 0 5.45 8.65a1 1 0 0 0-.3-.258 3 3 0 0 0-.428-.198l-.808-.295a76 76 0 0 0-1.364-.493C2.253 7.3 2 7.208 1.783 7.14c-.041-.013-.087-.025-.124-.038a2.1 2.1 0 0 0-.606-.116.72.72 0 0 0-.572.245 2 2 0 0 0-.105.132 1.6 1.6 0 0 0-.155.309c-.15.443-.225.908-.22 1.376.002.423.013.966.246 1.334a.8.8 0 0 0 .22.24c.166.114.333.129.507.141.26.019.513-.045.764-.103l2.447-.566z" />
                      </svg>
                    </div>
                    <span className="font-medium">Yelp</span>
                  </button> */}
                </div>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 lg:px-8 pb-6">
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(100%, 20rem), 1fr))'
            }}>
            {/* Facebook */}
            <div className="min-w-0 p-5 rounded-xl border-2 border-border bg-background hover:border-primary/30 transition-colors relative">
              {selectedClient?.socialnetworks?.facebook &&
                selectedClient?.socialnetworks?.fbOAuth === true &&
                isSuperAdmin && (
                  <div className="absolute top-4 right-4">
                    <div
                      className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center"
                      title="OAuth Connected">
                      <svg
                        className="h-3.5 w-3.5 text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                  </div>
                )}
              <div className="flex items-center gap-4 mb-4">
                <div className="h-14 w-14 rounded-full bg-blue-600 flex items-center justify-center shadow-lg flex-shrink-0">
                  <Facebook className="h-7 w-7 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-lg">Facebook</p>
                  {selectedClient?.socialnetworks?.facebook ? (
                    links?.Facebook_URL ? (
                      <a
                        className="text-sm text-muted-foreground truncate block"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={links?.Facebook_URL}>
                        {selectedClient?.socialnetworks?.facebookPage ||
                          'Connected'}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground truncate">
                        {selectedClient?.socialnetworks?.facebookPage ||
                          'Connected'}
                      </p>
                    )
                  ) : (
                    <p className="text-sm text-muted-foreground truncate">
                      Not connected
                    </p>
                  )}
                </div>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {selectedClient?.socialnetworks?.facebook ? (
                  <>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Connected
                    </Badge>
                    {!isMobile && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 rounded-lg"
                        style={{
                          background: 'none',
                          cursor: 'pointer',
                          outline: 'inherit'
                        }}
                        onClick={() => {
                          disconnectSocialNetwork(
                            'facebook',
                            selectedClient?.id
                          );
                        }}>
                        <Unplug className="h-4 w-4" />
                        Disconnect
                      </Button>
                    )}
                  </>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                    <X className="h-4 w-4 mr-1.5" /> Disconnected
                  </Badge>
                )}
              </div>
            </div>

            {/* Instagram */}
            <div className="min-w-0 p-5 rounded-xl border-2 border-border bg-background hover:border-primary/30 transition-colors relative">
              {selectedClient?.socialnetworks?.instagram &&
                selectedClient?.socialnetworks?.igOAuth === true &&
                isSuperAdmin && (
                  <div className="absolute top-4 right-4">
                    <div
                      className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center"
                      title="OAuth Connected">
                      <svg
                        className="h-3.5 w-3.5 text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                  </div>
                )}
              <div className="flex items-center gap-4 mb-4">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center shadow-lg flex-shrink-0">
                  <Instagram className="h-7 w-7 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-lg">Instagram</p>
                  {selectedClient?.socialnetworks?.instagram ? (
                    links?.Instagram_URL ? (
                      <a
                        className="text-sm text-muted-foreground truncate block"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={links?.Instagram_URL}>
                        {selectedClient?.socialnetworks?.instagramPage ||
                          'Connected'}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground truncate">
                        {selectedClient?.socialnetworks?.instagramPage ||
                          'Connected'}
                      </p>
                    )
                  ) : (
                    <p className="text-sm text-muted-foreground truncate">
                      Not connected
                    </p>
                  )}
                </div>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {selectedClient?.socialnetworks?.instagram ? (
                  <>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Connected
                    </Badge>
                    {!isMobile && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 rounded-lg"
                        style={{
                          background: 'none',
                          cursor: 'pointer',
                          outline: 'inherit'
                        }}
                        onClick={() => {
                          disconnectSocialNetwork(
                            'instagram',
                            selectedClient?.id
                          );
                        }}>
                        <Unplug className="h-4 w-4" />
                        Disconnect
                      </Button>
                    )}
                  </>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                    <X className="h-4 w-4 mr-1.5" /> Disconnected
                  </Badge>
                )}
              </div>
            </div>

            {/* Google */}
            <div className="order-first min-w-0 p-5 rounded-xl border-2 border-border bg-background hover:border-primary/30 transition-colors relative">
              {selectedClient?.socialnetworks?.googleMyBusiness &&
                selectedClient?.socialnetworks?.gOAuth === true &&
                isSuperAdmin && (
                  <div className="absolute top-4 right-4">
                    <div
                      className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center"
                      title="OAuth Connected">
                      <svg
                        className="h-3.5 w-3.5 text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                  </div>
                )}
              <div className="flex items-center gap-4 mb-4">
                <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center shadow-lg flex-shrink-0">
                  <svg className="h-7 w-7" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-lg">Google</p>
                  {selectedClient?.socialnetworks?.googleMyBusiness ? (
                    links?.GMB_Google_2_URL ||
                    links?.Google_Business_Listing_URL ? (
                      <a
                        className="text-sm text-muted-foreground truncate block"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={
                          links?.GMB_Google_2_URL ||
                          links?.Google_Business_Listing_URL
                        }>
                        {selectedClient?.socialnetworks
                          ?.googleMyBusinessLocationName || 'Connected'}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground truncate">
                        {selectedClient?.socialnetworks
                          ?.googleMyBusinessLocationName || 'Connected'}
                      </p>
                    )
                  ) : (
                    <p className="text-sm text-muted-foreground truncate">
                      Not connected
                    </p>
                  )}
                </div>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {selectedClient?.socialnetworks?.googleMyBusiness ? (
                  <>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Connected
                    </Badge>
                    {!isMobile && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 rounded-lg"
                        style={{
                          background: 'none',
                          cursor: 'pointer',
                          outline: 'inherit'
                        }}
                        onClick={() => {
                          disconnectSocialNetwork(
                            'googleMyBusiness',
                            selectedClient?.id
                          );
                        }}>
                        <Unplug className="h-4 w-4" />
                        Disconnect
                      </Button>
                    )}
                  </>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                    <X className="h-4 w-4 mr-1.5" /> Disconnected
                  </Badge>
                )}
              </div>
            </div>

            {TIKTOK_ENABLED && (
              <div className="min-w-0 p-5 rounded-xl border-2 border-border bg-background hover:border-primary/30 transition-colors relative">
                {tiktokConnected && isSuperAdmin && (
                  <div className="absolute top-4 right-4">
                    <div
                      className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center"
                      title="OAuth Connected">
                      <svg
                        className="h-3.5 w-3.5 text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-14 w-14 rounded-full bg-black flex items-center justify-center shadow-lg flex-shrink-0">
                    <svg
                      className="h-7 w-7 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-lg">TikTok</p>
                    {tiktokConnected ? (
                      tiktokProfileUrl ? (
                        <a
                          className="text-sm text-muted-foreground truncate block"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={tiktokProfileUrl}>
                          {tiktokDisplayName}
                        </a>
                      ) : (
                        <p className="text-sm text-muted-foreground truncate">
                          {tiktokDisplayName}
                        </p>
                      )
                    ) : (
                      <p className="text-sm text-muted-foreground truncate">
                        Not connected
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {tiktokConnected ? (
                    <>
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Connected
                      </Badge>
                      {!isMobile && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 rounded-lg"
                          onClick={() => {
                            disconnectSocialNetwork(
                              'tiktok',
                              selectedClient?.id
                            );
                          }}>
                          <Unplug className="h-4 w-4" />
                          Disconnect
                        </Button>
                      )}
                    </>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                      <X className="h-4 w-4 mr-1.5" /> Disconnected
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Bing */}
            {showBing && (
              <div className="min-w-0 p-5 rounded-xl border-2 border-border bg-background hover:border-primary/30 transition-colors relative">
                {bingConnected && isSuperAdmin && (
                  <div className="absolute top-4 right-4">
                    <div
                      className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center"
                      title="OAuth Connected">
                      <svg
                        className="h-3.5 w-3.5 text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-14 w-14 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-lg flex-shrink-0 overflow-hidden">
                    <img
                      src={BingLogo}
                      alt="Bing"
                      className="h-10 w-10 object-contain"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-lg">Bing</p>
                    {bingConnected ? (
                      <p className="text-sm text-muted-foreground truncate">
                        {bingAccountName || 'Connected'}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground truncate">
                        Not connected
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {bingConnected ? (
                    <>
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Connected
                      </Badge>
                      {!isMobile && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 rounded-lg"
                          style={{
                            background: 'none',
                            cursor: 'pointer',
                            outline: 'inherit'
                          }}
                          onClick={() => {
                            disconnectSocialNetwork('bing', selectedClient?.id);
                          }}>
                          <Unplug className="h-4 w-4" />
                          Disconnect
                        </Button>
                      )}
                    </>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                      <X className="h-4 w-4 mr-1.5" /> Disconnected
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Disconnect Confirmation Modal */}
      <Dialog open={disconnectModalOpen} onOpenChange={setDisconnectModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              {`Disconnect ${
                disconnectNetwork === 'googleMyBusiness'
                  ? 'Google'
                  : disconnectNetwork.charAt(0).toUpperCase() +
                    disconnectNetwork.slice(1)
              }`}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              {`Are you sure you want disconnect ${
                disconnectNetwork === 'googleMyBusiness'
                  ? 'google'
                  : disconnectNetwork
              }?`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={confirmDisconnect}
              disabled={isDisconnecting}>
              {isDisconnecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'OK'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setDisconnectModalOpen(false)}
              disabled={isDisconnecting}
              className="px-8 h-11 text-base font-semibold rounded-lg">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={handleSuccessClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              {successTitle}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground text-center">
              {successDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button onClick={handleSuccessClose} className="h-11 px-8">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-center text-red-600">
              {errorTitle}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground text-center mt-2">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => setErrorModalOpen(false)}
              variant="outline"
              className="h-11 px-8">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* TikTok Premium Modal */}
      <Dialog open={premiumModalOpen} onOpenChange={setPremiumModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <Crown className="h-7 w-7 text-primary" />
            </div>
            <DialogTitle className="text-2xl font-bold text-foreground">
              Premium Feature
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              This is a Premium Feature. Please contact us to upgrade your plan
              and connect TikTok.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setPremiumModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Links;
